import React from 'react';
import { motion } from 'framer-motion';
import { ScanSearch, Sparkles, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shared/components/ui/card';

/**
 * Transparency panel — replaces the old "Adjust Crop Position" UI.
 * Shows two sections:
 *   1. From your site: favicon, og:image, title, description, hero tagline, theme color, font hint
 *   2. Our art director chose: direction, tagline, palette, hero metaphor, used-og-image flag
 *
 * Missing fields hide their row (no "N/A" placeholders).
 */
const ColorSwatch = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-5 h-5 rounded border border-border/60 shadow-sm"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
    <span className="font-mono text-xs text-muted-foreground">{color}</span>
    {label && <span className="text-xs text-muted-foreground">· {label}</span>}
  </div>
);

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[120px_1fr] items-start gap-3 py-1.5 text-sm">
    <div className="text-xs uppercase tracking-wide text-muted-foreground pt-0.5">{label}</div>
    <div className="text-foreground break-words">{children}</div>
  </div>
);

export const ElementsCaptured = ({ elementsCaptured: e }) => {
  if (!e) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="shadow-sm border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-display flex items-center gap-2 text-foreground">
            <ScanSearch className="w-5 h-5 text-primary" />
            Elements Captured
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            What we read from your site, and what our art director chose.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Section 1: From your site */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              From your site
            </div>
            <div className="flex items-start gap-4 mb-3">
              {e.favicon_url && (
                <img
                  src={e.favicon_url}
                  alt=""
                  className="w-8 h-8 rounded shrink-0"
                  onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
                />
              )}
              {e.og_image_url && (
                <div className="relative">
                  <img
                    src={e.og_image_url}
                    alt="og:image preview"
                    className="w-32 h-16 object-cover rounded border border-border/50"
                    onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
                  />
                  {e.used_og_image && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground font-medium">
                      Used as ref
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="divide-y divide-border/40">
              {e.title && <Row label="Title">{e.title}</Row>}
              {e.description && <Row label="Description"><span className="text-muted-foreground">{e.description}</span></Row>}
              {e.hero_tagline && <Row label="Hero line"><em className="text-foreground">"{e.hero_tagline}"</em></Row>}
              {e.theme_color && <Row label="Theme color"><ColorSwatch color={e.theme_color} /></Row>}
              {e.font_hint && <Row label="Font hint">{e.font_hint}</Row>}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border/50" />

          {/* Section 2: Our art director chose */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Our art director chose
            </div>
            <div className="divide-y divide-border/40">
              {e.persona_type && (
                <Row label="Persona">
                  <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-muted text-foreground font-mono">
                    {e.persona_type.toLowerCase().replace(/_/g, ' ')}
                  </span>
                </Row>
              )}
              <Row label="Direction">
                <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
                  {e.direction_name}
                </span>
              </Row>
              <Row label="Tagline">
                <div className="font-medium text-foreground">"{e.tagline}"</div>
                {e.secondary_tagline && (
                  <div className="text-xs text-muted-foreground mt-0.5">"{e.secondary_tagline}"</div>
                )}
              </Row>
              <Row label="Palette">
                <div className="flex gap-4">
                  <ColorSwatch color={e.primary_color} label="primary" />
                  <ColorSwatch color={e.secondary_color} label="accent" />
                </div>
              </Row>
              <Row label="Hero metaphor">
                <em className="text-muted-foreground">{e.hero_metaphor}</em>
              </Row>
              <Row label="Visual reference">
                <span className="inline-flex items-center gap-1.5 text-xs">
                  {e.used_og_image ? (
                    <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> og:image passed to the model as style reference</>
                  ) : (
                    <><XCircle className="w-3.5 h-3.5 text-muted-foreground" /> none (text-only generation)</>
                  )}
                </span>
              </Row>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ElementsCaptured;
